// React
import React, { useRef, useState, useEffect, ChangeEvent, MutableRefObject } from 'react';

// Fluent UI
import { Checkbox, Panel, DefaultButton, TextField as FluentTextField, SpinButton} from '@fluentui/react';
import { SparkleFilled } from '@fluentui/react-icons';
import type { RadioProps } from '@fluentui/react-components';
import { makeStyles, Radio, tokens, useId } from '@fluentui/react-components';

// Material UI
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Grid, Container, Typography, IconButton, Box, TextField, Popover, Button } from '@mui/material';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import ListOutlinedIcon from '@mui/icons-material/ListOutlined';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Tooltip as MTooltip } from '@mui/material';
import { styled } from '@mui/system';

// PrimeReact
import { FileUpload } from 'primereact/fileupload';
import { Card } from 'primereact/card';
import { Button as PrimeButton } from 'primereact/button';
import { Dropdown as PrimeDropdown } from 'primereact/dropdown';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { Dialog } from 'primereact/dialog';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Toast } from "primereact/toast";
import { ProgressBar } from 'primereact/progressbar';
import { InputText } from 'primereact/inputtext';
import { Message as PrimeMessage } from 'primereact/message';
import { Tooltip } from 'primereact/tooltip';

// Azure
import { BlobServiceClient } from '@azure/storage-blob';

// UUID
import {v4 as uuidv4} from 'uuid';

// Local imports
import styles from './Chat.module.css';
import "./FeedbackButton.css";
import { chatApi, Approaches, AskResponse, ChatRequest, ChatTurn } from '../../api';
import { Answer, AnswerError, AnswerLoading } from '../../components/Answer';
import { QuestionInput } from '../../components/QuestionInput';
import { UserChatMessage } from '../../components/UserChatMessage';
import { AnalysisPanel, AnalysisPanelTabs } from '../../components/AnalysisPanel';
import { SettingsButton } from '../../components/SettingsButton';
import { ClearChatButton } from '../../components/ClearChatButton';
import {ExampleList, Example} from "../../components/Example";
import { Upload } from '../../components/chat/Upload';
import { CHAT_FILES_MAX_SIZE } from '../../utils/app/const';
import { humanFileSize } from '../../utils/app/files';
import { Conversation, KeyConfiguration, KeyValuePair, Message, ModelType } from '../../types';
import { updateConversation } from '../../utils/app/conversation';
import YouTube, { YouTubeProps } from 'react-youtube';

// LocalStorage
import ls from 'localstorage-slim';

const useStyles = makeStyles({
  field: {
    display: 'grid',
    gridRowGap: tokens.spacingVerticalS,
  },
});
interface dropdown {
  label: string;
  value: string;
}
interface Props {
  conversation: Conversation;
  keyConfiguration: KeyConfiguration;
  messageIsStreaming: boolean;
  loading: boolean;
  onSend: (message: Message, deleteCount?: number) => void;
  onUpdateConversation: (
    conversation: Conversation,
    data: KeyValuePair,
  ) => void;
  onEditMessage: (message: Message, messageIndex: number) => void;
  stopConversationRef: MutableRefObject<boolean>;
  handleKeyConfigurationValidation: () => boolean;
}
/**
 * Represents the Chat component.
 *
 * @param {Partial<RadioProps>} props - The props for the Chat component.
 * @returns {JSX.Element} The rendered Chat component.
 **/
const Chat = (props: Partial<RadioProps>) => {
  const HighlightOnHover = styled('div')({
    '&:hover .highlightIcon': {
      position: 'relative',
      boxShadow: '0 0 10px 5px rgba(169, 169, 169, 0.7)',
      borderRadius: '5px',
      zIndex: '1',
      transition: 'box-shadow 0.3s ease-in-out',
    },
  });

  const [disabled, setdisabled] = useState(false);
  const [selectedProject, setSelectedProject] = useState("");
  const radioName = useId('radio');
  const _styles = useStyles();
  const labelId = useId('label');
  const indexes = process.env.REACT_APP_EXCLUDE_PROJECTS?.split(',') || 'PPTs,Apple Marcom,AWS Agent'.split(',');
  const hyde_indexes = process.env.REACT_APP_HYDE_INDEXES?.split(',') || "PPTs,Apple Marcom,NewFI Updated,Acra Lending Hyde,pss-kb".split(',');
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
  const [promptTemplate, setPromptTemplate] = useState<string>('');
  const [retrieveCount, setRetrieveCount] = useState<number>(10);
  const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
  const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
  const [excludeCategory, setExcludeCategory] = useState<string>('');
  const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const lastQuestionRef = useRef<string>('');
  const lastImageRef = useRef<string>('');
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const [quickChatFile, setQuickChatFile] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [activeCitation, setActiveCitation] = useState<string>();
  const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

  const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
  const [question, setQuestion] = useState<string>('');
  const [answers, setAnswers] = useState<[user: string, response: AskResponse][]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [isUploadSuccess, setIsUploadSuccess] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState<Conversation>();
  const [chatInfo, setChatInfo] = useState<any>(null);
  const [examples, setExamples] = useState<string>("");
  const handleKeyConfigurationValidation = (): boolean => {
    return true;
  };
  var [projectsDetails, setProjectsDetails] = React.useState([]);
  var [projectSourceDetails, setProjectSourceDetails] = React.useState([]);
  const [feedback, setFeedback] = useState(null);

  const [isThumbsUp, setIsThumbsUp] = useState(false);
  const [isThumbsDown, setIsThumbsDown] = useState(false);
  const [username, setUsername] = useState("");
  const [success, setSuccess] = useState(false);
  const [chatError, setChatError] = useState<unknown>();
  const [error, setError] = useState(false);
  const toast = useRef<Toast>(null);
  const [quickChatIndex, setQuickChatIndex] = useState('');
  const [quickChatFileName, setQuickChatFileName] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeSummary, setYoutubeSummary] = useState('');
  const [youtubeTopics, setYoutubeTopics] = useState('');
  const [youtubeTokenUsage, setYoutubeTokenUsage] = useState('');
  const [youtubeTopicsUsage, setYoutubeTopicsUsage] = useState('');
  const [visible, setVisible] = useState(false);
  const [youtubeTitle, setYoutubeTitle] = useState("");
  const [youtubeMetadata, setYoutubeMetadata] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [display, setDisplay] = useState("");
  const [quickChatSummary, setQuickChatSummary] = useState("");
  const [quickChatTopics, setQuickChatTopics] = useState("");
  const [quickChatTokenUsage, setQuickChatTokenUsage] = useState("");
  const [quickChatTopicsUsage, setQuickChatTopicsUsage] = useState("");
  var project_index_mapping = null;
  var project_examples_mapping = null;
  var project_prompt_prefix_mapping = null;
  //@ts-ignore
  var project_source_mapping = null;
  const [projectSourceMapping, setProjectSourceMapping] = useState(null);
  const [projectExamplesMapping, setProjectExamplesMapping] = useState(null);
  const [projectPromptPrefixMapping, setprojectPromptPrefixMapping] = useState(null);
  const [projectIndexMapping, setProjectIndexMapping] = useState(null);
  var projlist = [];
  var [project, setProject] = React.useState('');
  var [projectIndex, setProjectIndex] = React.useState('');
  var projectmenulist = [{label:"None", value:"None"}];
  var projectSourceList = [{label:"None", value:"None"}];
  const [llmmodel, setLlmModel] = React.useState('GPT4');
  const [isStreaming, setIsStreaming] = useState<boolean>(true);
  const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: AskResponse][]>([]);
  const [fileName, setFileName] = useState("");
  const [filePath, setFilePath] = useState(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef(null);
  const [costDisplay, setCostDisplay] = useState("true");

  /**
   * Handles the change event when a new alignment is selected.
   * 
   * @param event - The click event.
   * @param newAlignment - The new alignment value.
   */
  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string,
  ) => {
    setLlmModel(newAlignment);
  };

  const handleCostToggle = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string,
  ) => {
    setCostDisplay(newAlignment);
  };

  useEffect(() => { 
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
    /**
     * Loads project information.
     */
    const load_project_info = async() => {
      const user_data = ls.get('token-gpt-app');
      
      //@ts-ignore
      if (user_data && user_data['email']) {
        //@ts-ignore
        var uname = user_data['email'].split('@')[0];
        setUsername(uname);

        // Comment below statement after testing
        // uname = 'tluser'

        fetch((process.env.REACT_APP_PROJECT_INFO_URL || ""),{
          //"https://func-azureopenai-utils-poc.azurewebsites.net/api/project_info?code=QASdNkz4Bowqr2HENWzc4QhFJvy0Pa_zwFXeTNnniSm5AzFu_YRL1w=="), {
          method: 'POST',
          body: JSON.stringify({ "user": uname })
        })
        .then((response) => {
          console.log('Projects Response : ', response)
          return response.json();
        })
        .then(data => {
          // Use the data from the response
          //@ts-ignore
          project_index_mapping = data.reduce((acc, item) => {
                
            // Extract "ProjectName" and "IndexName" from each item
            const { ProjectName, IndexName } = item;

            // Create the key-value pair in the accumulator
            acc[ProjectName] = IndexName;

            return acc;
            // }
          }, {});
          //@ts-ignore
          project_source_mapping = data.reduce((acc, item) => {
                
            // Extract "ProjectName" and "IndexName" from each item
            const { ProjectName, SourcePage } = item;

            // Create the key-value pair in the accumulator
            acc[ProjectName] = SourcePage;

            return acc;
            // }
          }, {});
          //@ts-ignore
          project_examples_mapping = data.reduce((acc, item) => {

                
            // Extract "ProjectName" and "Examples" from each item
            const { ProjectName, Examples } = item;

            // Create the key-value pair in the accumulator
            acc[ProjectName] = JSON.parse(Examples)

            return acc;
            // }
          }, {});
          //@ts-ignore
          project_prompt_prefix_mapping = data.reduce((acc, item) => {
            // Extract "ProjectName" and "Prompt Prefix" from each item
            const { ProjectName, PromptPrefix } = item;

            // Create the key-value pair in the accumulator
            acc[ProjectName] = PromptPrefix;

            return acc;
          }, {});
          // console.log('project examples mapping : ', project_examples_mapping)
          // console.log('project prompt prefix mapping : ', project_prompt_prefix_mapping)
          setProjectSourceMapping(project_source_mapping)
          setProjectExamplesMapping(project_examples_mapping)
          setprojectPromptPrefixMapping(project_prompt_prefix_mapping)
          setProjectIndexMapping(project_index_mapping)
          // console.log('project index mapping : ', project_index_mapping)
          projlist = Object.keys(project_index_mapping)
          // console.log('KEYS in project index mapping : ', projlist)
          // @ts-ignore
          projectmenulist = Object.entries(project_index_mapping).map(([key, value]) => ({
            label: key,
            value: value,
          }));
          //@ts-ignore
          projectSourceList = Object.entries(project_source_mapping).map(([key, value]) => ({
            label: key,
            value: value,
          }));

          projectmenulist.unshift({label:"None", value:"None"})
          
          // console.log('projindmap : ', projectmenulist)
          //@ts-ignore
          setProjectsDetails(projectmenulist)
          //@ts-ignore
          setProjectSourceDetails(projectSourceList)
          // Set initial project if not already set
          if (!selectedProject && projectmenulist.length > 0) {
            //@ts-ignore
            setSelectedProject(projectmenulist[0].label)
          }
          // console.log('VALUES in project index mapping : ', Object.values(project_index_mapping))
        })
        .catch((err) => {
          console.log(err.message);
        });
      }
    }
      load_project_info()
  }, [isLoading]);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'popup-button-popover' : undefined;

  const audioLoader = () => {
    const fileType = fileName.split('.').pop()!;
    //@ts-ignore
    const fileURL: string = URL.createObjectURL(filePath);
    if(fileType=="mp3"){
      return (
        <div className="App">
            <figure>
                <figcaption>Audio:</figcaption>
                <audio
                    controls
                    src={fileURL}>
                    Your browser does not support the
                    <code>audio</code> element.
                </audio>
            </figure>
        </div>
      );
    }
  }

  const youtube_iframe = () => {
    const onPlayerReady: YouTubeProps['onReady'] = (event:any) => {
      // access to player in all event handlers via event.target
      event.target.pauseVideo();
    }
    const Id = youtubeUrl.split("=")[1]
    const opts: YouTubeProps['opts'] = {
      height: '300',
      width: '100%',
      playerVars: {
        // https://developers.google.com/youtube/player_parameters
        autoplay: 0,
      },
    };
  
    return <YouTube videoId={Id} opts={opts} onReady={onPlayerReady} />;
  }

  const IndexFiles = async (req:any) =>{
    const url = process.env.REACT_APP_QUICK_CHAT_DOC_LOADER_URL || 
    ""
    // "http://localhost:7071/api/llm_doc_loader"
    const headers = {"Content-Type":"application/json"}
    const resp = await fetch(url, {
        method:"POST",
        headers:headers,
        body:JSON.stringify(req)
    });
    const file_name = req['file_data'][0]['file_name']
    // setQuickChatFileName(file_name);
    if(resp!=undefined && resp.status==200){
        const resp_json = await resp.json();
        const summary = resp_json[0][file_name]["summary"]
        const topics = resp_json[0][file_name]["topics"]
        const summary_usage = resp_json[0][file_name]["summary_usage"]
        const topics_usage = resp_json[0][file_name]["topics_usage"]
        const metadata = resp_json[0][file_name]["metadata"]
        setQuickChatSummary(summary)
        setQuickChatTopics(topics)
        setQuickChatTokenUsage(summary_usage)
        setQuickChatTopicsUsage(topics_usage)
    }else{
      showError("Error loading file. Please try again.");
      // console.log(resp)
    }

  }

  const readFileAsBytes = (file:any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
  
      reader.onload = (event) => {
        //@ts-ignore
        let fileContent = Array.from(new Uint8Array(event.target?.result));
        // fileContent = Array.from(fileContent);
        // console.log(fileContent)
        resolve(fileContent);
      };
  
      reader.onerror = (event) => {
        reject(event.target?.error);
      };
  
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFile = async (files: File[]) => {
    // console.log(files)
    let req = {
      "model":process.env.REACT_APP_DEFAULT_MODEL || "gpt-4-32k",
      "deployment":process.env.REACT_APP_DEFAULT_APPROACH || Approaches.GPT432K,
      "index":"quick-chat-index",
      "app_name":"knowledge_ai",
      "file_data" : []
    }
    //@ts-ignore
    setFileName(files[0].name)
    //@ts-ignore
    setFilePath(files[0])
    //@ts-ignore
    let file_req = []
    for(const file of files){
      const file_item = {}
      // console.log(file)
      //@ts-ignore
      file_item['file_name'] = file.name
      //@ts-ignore
      file_item['file_content'] = await readFileAsBytes(file)
      file_req.push(file_item)
    }
    //@ts-ignore
    req['file_data'] = file_req
      if (!handleKeyConfigurationValidation()) {
          return;
      }
      if (!validateFile(files[0])) {
          handleIsUploadSuccess(false);
          return;
      }

      handleIsUploading(true);

      try {
          await IndexFiles(req);
          // await uploadFile(files[0]);
          showSuccess("Files are indexed")
          handleIsUploading(false);
          handleIsUploadSuccess(true)
      } catch (e) {
          console.error(e);
          handleUploadError((e as Error).message);
          handleIsUploading(false);
          handleIsUploadSuccess(false)
      }
  };

  // Function to handle clicking on the Browse icon
  const handleBrowseClick = () => {
    // Add your logic here for browsing files
    // For example, you can trigger a file input click event
    // or open a file dialog
    console.log('Browse icon clicked');
    //@ts-ignore
    fileInputRef.current.click();
    // Add your logic here for browsing files
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // console.log(event.target)
    const selectedFile = event.target.files?.[0];
    // console.log(selectedFile)
    if (selectedFile) {
      setUploadedFile(selectedFile);
    }
   
  };

  const load_youtube_url = async (url:any) => {
    setdisabled(true);
    if(url!=undefined && url!=""){
     const req_url = process.env.REACT_APP_YOUTUBE_LOADER ||  
    //  "https://func-azureopenai-utils-poc.azurewebsites.net/api/youtube_loader?code=hBNwg3Shm_fgXZslTfHsXNDh3DpWzGs5GnXaWgxsEVbhAzFuahtp2A=="
    //  "http://localhost:7071/api/youtube_loader"
    ""
      const headers = {"Content-Type":"application/json"}
      const req = {
          "url":url,
          "model":"gpt-4-32k",
          "deployment":process.env.REACT_APP_DEFAULT_APPROACH || Approaches.GPT432K,
          "index":"youtube-index",
          "app_name":"knowledge-ai"
      }
      const resp = await fetch(req_url, {
          method:"POST",
          headers:headers,
          body:JSON.stringify(req)
      });
      if(resp!=undefined && resp.status==200){
          const resp_json = await resp.json();
          const summary = resp_json["summary"]
          const topics = resp_json["topics"]
          const summary_usage = resp_json["summary_usage"]
          const topics_usage = resp_json["topics_usage"]
          const metadata = resp_json["metadata"]
          const youtubeTitle = metadata['title']
          setYoutubeTitle(youtubeTitle)
          setYoutubeMetadata(metadata)
          setYoutubeSummary(summary)
          setYoutubeTopics(topics)
          setYoutubeTokenUsage(summary_usage)
          setYoutubeTopicsUsage(topics_usage)
      }else{
          console.log("Error loading youtube url. Please try again.");
          showError("Error loading youtube url. Please try again.");
      }
    }
    setdisabled(false);
  }

  const uploadFile = async (file: File) => {
    const fileNameId = uuidv4();
    const fileType = file.name.split('.').pop()!;
    await uploadBlobFromBuffer(fileNameId, file, file.name)
  }

  const showSuccess = (text:string) => {
      toast.current?.show({severity:'success', summary: 'Success', detail:text, life: 3000});
  }
  
  const showInfo = (text:string) => {
      toast.current?.show({severity:'info', summary: 'Info', detail:text, life: 3000});
  }
  
  const showWarn = (text:string) => {
      toast.current?.show({severity:'warn', summary: 'Warning', detail:text, life: 3000});
  }
  
  const showError = (text:string) => {
      toast.current?.show({severity:'error', summary: 'Error', detail:text, life: 3000});
  }

  const validateFile = (file: File) => {
      console.log(`upload file size: ${humanFileSize(file.size)}`);
      console.log(`file max size: ${humanFileSize(CHAT_FILES_MAX_SIZE)}`);
      if (CHAT_FILES_MAX_SIZE != 0 && file.size > CHAT_FILES_MAX_SIZE) {
          handleUploadError(`Please select a file smaller than ${humanFileSize(CHAT_FILES_MAX_SIZE)}`);
          return false;
      }

      console.log(`upload file type: ${file.name.split('.').pop()!}`);
      if (!validateFileType(file.name.split('.').pop()!)) {
          handleUploadError(`Please upload file of these types: ${supportFileType}`);
          return false;
      }

      return true;
  };

  const supportFileType = "pdf, epub, docx, txt, md, csv, json, zip";

  function validateFileType(fileType: string): boolean {
      switch (fileType) {
          case "pdf":
          case "epub":
          case "docx":
          case "txt":
          case "md":
          case "csv":
          case "zip":
          case "json":
          case "wav":
          case "mp3":
              return true;
          default:
              return false;
      }
  }

  const fetchUsername = () =>{
      const user_data: any = ls.get('token-gpt-app');
      // console.log(user_data)
      const email = user_data['email'].split("@")[0]
      // console.log(email)
      setUsername(email)
      return email
  }


  //@ts-ignore
  const save_index_status = async(status_url, email, file_name, session_id, app_name, index_name) =>{
      const req = {
          "status_url":status_url,
          "username":email,
          "file_name":file_name,
          "session_id":session_id,
          "app_name":app_name,
          "index_name":index_name
      }
      const headers = {"Content-Type":"application/json"}
      const url = process.env.REACT_APP_SAVE_INDEX_URL || ""
      //"https://prompt-lib-func.azurewebsites.net/api/save_quick_chat_info?code=gB5NDR_09-eYo4RLpanMrjMpN1Abt3gJ-xveNHLRyBgAAzFuMChcfQ=="
      const resp = await fetch(url, {
          method:"POST",
          headers:headers,
          body:JSON.stringify(req)
      })
      if(resp!=undefined && resp.status==200){
          // const resp_json = await resp.json();
          // console.log(resp_json)
          // console.log(resp)
      }else{
          console.log("Error saving index status. Please try again.");
          // showError("Error saving index status. Please try again.");
      }
      // get_chat_info()
  }

  const index_docs = async (file_name:string) => {
      const session_id=1
      const email = fetchUsername()
      const url = process.env.REACT_APP_INDEX_DOCS_URL || ""
      //"http://localhost:7072/api/orchestrators/index_orchestrator"
      const headers = {"Content-Type":"application/json"}
      const storage_account = process.env.REACT_APP_STORAGE_ACCOUNT || "stazureopenaidev"
      const req_body = {
          "storage_account":storage_account,
          "input_container":process.env.REACT_APP_QUICK_CHAT_CONTAINER || "quick-chat",
          "output_container":"content",
          "repo_type":'blob',
          "skipblobs":"false",
          "TENANT_ID":process.env.REACT_APP_TENANT_ID,
          "SEARCH_SERVICE":process.env.REACT_APP_SEARCH_SERVICE,
          "SEARCH_KEY":process.env.REACT_APP_SEARCH_KEY,
          "SEARCH_INDEX":process.env.REACT_APP_QUICK_CHAT_INDEX,
          "STORAGE_KEY":process.env.REACT_APP_STORAGE_KEY,
          "FORM_RECOGN_SERVICE":"",
          "FORM_RECOG_KEY":"",
          "remove":"false",
          "removeall":"false",
          "localpdfparser":"true",
          "default_parser":"false"
      }
      const resp = await fetch(url, {
          method:"POST",
          headers:headers,
          body:JSON.stringify(req_body)
      })
      if(resp!=undefined && resp.status==202){
         const resp_json = await resp.json();
        //  console.log(resp_json)
         showSuccess("Indexing data...");
         await save_index_status(resp_json['statusQueryGetUri'], email, file_name, session_id, "knowledge_ai", "quick-chat-index")
         ls.set('quick-chat-info', JSON.stringify({"filename":file_name, "index":"quick-chat-index"}), {ttl: 86400});
        //  const chat_info = JSON.parse(ls.get('chat_info'))
        //@ts-ignore
         setQuickChatFile(file_name)
         setQuickChatIndex("quick-chat-index")
      }else{
          console.log("Error Indexing data. Please try again.");
          showError("Error Indexing data. Please try again.");
      }
  }
  
  const uploadBlobFromBuffer = async (fileNameId:string, file_data:any, fileName:string) => {
      const email = fetchUsername();
      const accountName = process.env.REACT_APP_ACCOUNT_NAME || "stazureopenaidev";
      const storageKey = process.env.REACT_APP_STORAGE_SAS_KEY
      const blobServiceClient = new BlobServiceClient(
          `https://${accountName}.blob.core.windows.net?${storageKey}`
        );
      const containerName = "quick-chat";
      const containerClient = await blobServiceClient.getContainerClient(containerName);
      // Create blob client from container client
      // console.log(fileName)
      const blockBlobClient = containerClient.getBlockBlobClient(email+"/"+fileName);
      // Upload buffer
      //@ts-ignore
      const res = await blockBlobClient.uploadData(file_data);
      if(res._response.status==201) {
              console.log("Uploaded successfully")
              setSuccess(true)
              showSuccess("Uploaded successfully")
              await index_docs(fileName)
              // console.log(quickChatFile);
      }else{
          console.log("Error uploading file");
          setError(true)
          showError("Error uploading file");
      }
  }

  const handleThumbsUpClick = async() => {
    const url = process.env.REACT_APP_FEEDBACK_URL || ""
    const headers = {"Content-Type":"application/json"}
    const req = {
      "app":process.env.APP_NAME||"knowledge_ai",
      "question":question,
      "response":answers[answers.length-1][1].answer,
      "session_id":1,
      "flag":"GOOD"
    }
    const resp = await fetch(url, {
      method:"POST",
      headers:headers,
      body:JSON.stringify(req)
    })
    if(resp!=undefined && resp.status==200){
      // const resp_json = await resp.json();
      // console.log(resp_json)
      setIsThumbsUp(true);
      setIsThumbsDown(false);
      showSuccess("Thank you for your feedback!");
    }else{
        console.log("Error recording your feedback. Please try again.");
        showError("Error recording your feedback. Please try again.");
    }
    
  };

  const handleThumbsDownClick = async() => {
    const url = process.env.REACT_APP_FEEDBACK_URL || ""
    const headers = {"Content-Type":"application/json"}
    const req = {
      "app":process.env.APP_NAME||"knowledge_ai",
      "question":question,
      "response":answers[answers.length-1][1].answer,
      "session_id":1,
      "flag":"BAD"
    }
    const resp = await fetch(url, {
      method:"POST",
      headers:headers,
      body:JSON.stringify(req)
    })
    if(resp!=undefined && resp.status==200){
      // const resp_json = await resp.json();
      // console.log(resp_json)
      setIsThumbsUp(false);
      setIsThumbsDown(true);
      showSuccess("Thank you for your feedback!");
    }else{
        console.log("Error recording your feedback. Please try again.");
        showError("Error recording your feedback. Please try again.");
    }
  };

    //@ts-ignore
    const get_chat_info = async (session_id, app_name, username) => {
      const url = process.env.REACT_APP_CHAT_INFO_URL || ""
      //"https://prompt-lib-func.azurewebsites.net/api/retrieve_chat_history?code=PTa_Zf6YAkrt0fjLfZihdzw6rTYkevh3Y0TbggUW1T8UAzFust_bpg=="
      const headers = {"Content-Type":"application/json"}
      const req = {
          "session_id":session_id,
          "app_name":app_name,
          "username":username
      }
      const resp = await fetch(url, {
          method:"POST",
          headers:headers,
          body:JSON.stringify(req)
      });
      if(resp!=undefined && resp.status==200){
          const resp_json = await resp.json();
          const chat_info = resp_json["quick_chat_info"][0]
          setChatInfo(chat_info)
      }else{
          console.log("Error fetching chat info. Please try again.");
          // showError("Error fetching chat info. Please try again.");
      }
  }

  const get_chat_type = () => {
      if (quickChatFile!=undefined && quickChatFile!=""){
          return "quick-chat"
      }
      else if(youtubeUrl!=undefined && youtubeUrl!=""){
          return "youtube"
      }
      else{
          return "chat"
      }
  }

  const makeApiRequest = async (question: string, uploadedFile:File|null, uploadedImage:string|null) => {
      lastQuestionRef.current = question;
      if(uploadedFile!=null){
        //@ts-ignore
        lastImageRef.current = uploadedImage;
      }
      else{
        lastImageRef.current = '';
      }
      setQuestion(question);
      chatError && setChatError(undefined);
      setIsLoading(true);
      setActiveCitation(undefined);
      setActiveAnalysisPanelTab(undefined);

      try {
        const history: ChatTurn[] = answers.map((a:any) => ({ user: a[0], bot: a[1].answer }));
        // const model = ls.get('model' || "gpt3516k"
        const request: ChatRequest = {
          history: [...history, { user: question, bot: undefined }],
          //@ts-ignore
          approach: llmmodel,
          overrides: {
            promptTemplate: promptTemplate.length === 0 ? undefined : promptTemplate,
            excludeCategory: excludeCategory.length === 0 ? undefined : excludeCategory,
            top: retrieveCount,
            semanticRanker: useSemanticRanker,
            semanticCaptions: useSemanticCaptions,
            suggestFollowupQuestions: useSuggestFollowupQuestions,
            documentOptions: examples
          },
        };
        
        // console.log(chat_info)
        console.log('API')
        //@ts-ignore
        //@ts-ignore
        let source_page = projectSourceMapping[quickChatIndex]
        //@ts-ignore
        const search_index = quickChatIndex
        const search_key = process.env.REACT_APP_SEARCH_KEY || ""
        const content_field = process.env.REACT_APP_CONTENT_FIELD || "content"
        const search_service = process.env.REACT_APP_SEARCH_SERVICE || "aimlcontextualsearch"
        const filename = quickChatFile
        const chat_type = get_chat_type()
        if(chat_type=="quick-chat"){
          source_page = process.env.REACT_APP_QUICK_CHAT_SOURCE_PAGE || "metadata"
        }
        //(quickChatFile!=undefined && quickChatFile!="") ? "quick-chat" : "chat"
        const result = await chatApi(request, source_page, search_index, search_key, content_field, search_service, filename, chat_type, youtubeUrl, username, youtubeTitle, projectPromptPrefixMapping, projectIndexMapping);
        setAnswers([...answers, [question, result]]);
      } catch (e) {
        setChatError(e);
      } finally {
        setIsLoading(false);
      }
    // }
  };

  const clearChat = () => {
    lastQuestionRef.current = '';
    chatError && setChatError(undefined);
    setActiveCitation(undefined);
    setActiveAnalysisPanelTab(undefined);
    setAnswers([]);
  };


  const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setPromptTemplate(newValue || '');
  };

  const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
    setRetrieveCount(parseInt(newValue || '3'));
  };

  const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    setUseSemanticRanker(!!checked);
  };

  const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    setUseSemanticCaptions(!!checked);
  };

  const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
    setExcludeCategory(newValue || '');
  };

  const onUseSuggestFollowupQuestionsChange = (
    _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    checked?: boolean
  ) => {
    setUseSuggestFollowupQuestions(!!checked);
  };

  const onExampleClicked = (example: string) => {
    makeApiRequest(example, null, null);
  };

  const onShowCitation = (citation: string, index: number) => {
    if (
      activeCitation === citation &&
      activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab &&
      selectedAnswer === index
    ) {
      setActiveAnalysisPanelTab(undefined);
    } else {
      setActiveCitation(citation);
      setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
    }

    setSelectedAnswer(index);
  };

  const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
    if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
      setActiveAnalysisPanelTab(undefined);
    } else {
      setActiveAnalysisPanelTab(tab);
    }

    setSelectedAnswer(index);
  };
  const handleUpdateConversation = (conversation: Conversation, data: KeyValuePair) => {
    const updatedConversation = {
      ...conversation,
      [data.key]: data.value,
    };

    const { single, all } = updateConversation(updatedConversation, conversations);

    setSelectedConversation(single);
    setConversations(all);
  };

  const handleIsUploading = (isUploading: boolean) => {
    setIsUploading(isUploading);
  };

  const handleIsUploadSuccess = (isUploadSuccess: boolean) => {
    setIsUploadSuccess(isUploadSuccess);
  };

  const handleUploadError = (errorMsg: string) => {
    showError(errorMsg);
  };

  function handleUpload(event: ChangeEvent<HTMLInputElement>): void {
    // throw new Error("Function not implemented.");
    // console.log(event);
  }

  const onUpload = () => {
    toast.current?.show({ severity: 'info', summary: 'Success', detail: 'File Uploaded' });
  };


const getDocumentOptions = () => { 
  if((examples!='')){
    return <ExampleList onExampleClicked={onExampleClicked}
                        projectExamples={projectExamplesMapping}
                        selectedProject={examples}
                         />
  }
}


const accept = () => {
  handleClose()
  setDisplay("none");
  //@ts-ignore
  // toast.current.show({ severity: 'info', summary: 'Confirmed', detail: 'You have accepted', life: 3000 });
  setSelectedProject("");
  setQuickChatFile("");
  setExamples("");
  clearChat();
  setQuickChatFileName("");
  setYoutubeTitle("");
  setYoutubeUrl("");
  setYoutubeSummary("");
  setYoutubeTopics("");
  setYoutubeTokenUsage("");
  setYoutubeTopicsUsage("");
  setQuickChatSummary("");
  setQuickChatTopics("");
  setDisplay("");
}


const onManualsOptionChanged = (option:any) => {
  
  handleClose();
  clearChat();
  setDisplay("none");
  
  setdisabled(true);
  setExamples(option||"")
  setSelectedProject(option)
  setQuickChatIndex(option)
  setdisabled(false);
}

const confirm2 = (event:any) => {
    confirmPopup ({
        target: event.currentTarget,
        message: 'Do you want to close this session?',
        // header: 'Confirmation',
        icon: 'pi pi-info-circle',
        acceptClassName: 'p-button-danger',
        accept,
        // reject
    });
};

const headerTemplate = (options:any) => {
  const { className, chooseButton, uploadButton, cancelButton } = options;
  // const value = totalSize / 10000;
  // const formatedValue = fileUploadRef && fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : '0 B';

  return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
          {chooseButton}
          {/* <div className="flex align-items-center gap-3 ml-auto">
              <span>{formatedValue} / 1 MB</span>
              <ProgressBar value={value} showValue={false} style={{ width: '10rem', height: '12px' }}></ProgressBar>
          </div> */}
      </div>
  );
};

const chooseOptions = { label:"Browse", icon: 'pi pi-fw pi-upload', iconOnly: true, className: 'custom-choose-btn p-button p-button-filled', style:{height:"30%"} };

  return (
    <>
    {/* <ConfirmDialog /> */}
    <ConfirmPopup />
    <Toast ref={toast}/>
    <Tooltip target=".custom-choose-btn" content="Choose" position="bottom" />
    {
        isUploading || disabled ? (
        <div className="card">
                <ProgressBar mode="indeterminate" style={{ height: '6px' }}>Processing</ProgressBar>
            </div>
        ) : undefined
    } 
    {/* <PopupButton/> */}
    {/* <div>
        <Dropdown.Menu show>
            <Dropdown.Item eventKey="1">
                <div className="card flex justify-content-center">
                <Uploady destination={{ url: "my-server.com/upload" }}>
                    <UploadDropZone 
                    onDragOverClassName="drag-over"
                    htmlDirContentParams={{ recursive: true }}
                    >
                        <span>Drag&Drop File(s) or Folder(s) Here</span>            
                    </UploadDropZone>
                </Uploady>
                </div>
            </Dropdown.Item>
            <Dropdown.Item eventKey="2">Another action</Dropdown.Item>
            <Dropdown.Item eventKey="3">Something else here</Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item eventKey="4">Separated link</Dropdown.Item>
        </Dropdown.Menu>
    </div> */}
    {/* <DocumentLoader /> */}
        {/* <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <span className="p-float-label">
                        <InputText disabled={disabled} id="sourceurl"  style={{width:"100%"}}  value={youtubeUrl}
                         onChange={async (e) => {setYoutubeUrl(e.target.value); await load_youtube_url(e.target.value) }} />
                        <label htmlFor="sourceurl">Source File Url (youtube)</label>
                    </span>
                </Grid>
            </Grid>
            </Box> */}
        
        {
        // @ts-ignore
        (quickChatFile!=undefined && quickChatFile!='' ) || (selectedProject!=undefined && selectedProject!="") || (youtubeSummary!="" && youtubeTokenUsage!="") ? (
         <div>
            {
            (quickChatFile!=undefined && quickChatFile!='' )? 
              (
                <>
                  <Box
                      height="auto"
                      alignContent="center"
                      sx={{
                        width: '100%',
                        // position: 'absolute',
                        left: '0',
                        right: '0',
                        marginRight: 'auto',
                        bottom: 10,
                        // backgroundColor: 'white',
                      }}
                    >
                      <div className="card flex flex-wrap gap-2 justify-content-center">
                        <PrimeButton disabled={true} icon="pi pi-file" style={{height:"2%", marginRight:"1%"}} className="p-button p-button"></PrimeButton>
                        <PrimeMessage severity="info" text={quickChatFile} />
                        <PrimeButton onClick={confirm2} icon="pi pi-times" className="p-button-danger p-button-outlined"></PrimeButton>
                      </div>
                    </Box>
                    <br/>
                      <div className="card" style={{width:"100%", justifyContent:"center", alignItems:"center"}}>
                        <Accordion>
                            <AccordionTab header="Quick Overview" disabled={disabled}>
                              {audioLoader()}
                                <Card title="Summary" >
                                    <p className="m-0" >
                                    {/* @ts-ignore */}
                                      {quickChatSummary}
                                    </p>
                                  </Card>
                                  <br/>
                                <Card title="Topics/Contents" >
                                    <p className="m-0" style={{"whiteSpace":"pre-wrap"}}>
                                    {/* @ts-ignore */}
                                      {quickChatTopics}
                                    </p>
                                  </Card>
                                  
                            </AccordionTab>
                        </Accordion>
                    </div>
                  </>
                ):
                (youtubeTitle!=undefined && youtubeTitle!="")?
                      (
                        <>
                        <Box
                              height="auto"
                              alignContent="center"
                              sx={{
                                width: '100%',
                                // position: 'absolute',
                                left: '0',
                                right: '0',
                                margin: 'auto',
                                bottom: 10,
                                // backgroundColor: 'white',
                              }}
                            >
                                <div className="card flex flex-wrap gap-2 justify-content-center">
                                    <PrimeButton disabled={true} icon="pi pi-youtube" style={{height:"2%", marginRight:"1%"}} className="p-button-danger p-button"></PrimeButton>
                                    <PrimeMessage severity="info" text={youtubeTitle} />
                                    <PrimeButton onClick={confirm2} icon="pi pi-times" className="p-button-danger p-button-outlined"></PrimeButton>
                                  </div>
                            </Box>
                            <br/>
                          <div className="card" style={{width:"100%", justifyContent:"center", alignItems:"center"}}>
                            <Accordion>
                                <AccordionTab header="Video Details" disabled={disabled}>
                                    {youtube_iframe()}
                                    <Card title="Summary" >
                                        <p className="m-0" >
                                        {/* @ts-ignore */}
                                          {youtubeSummary}
                                        </p>
                                      </Card>
                                      <br/>
                                    <Card title="Topics/Contents" >
                                        <p className="m-0" style={{"whiteSpace":"pre-wrap"}}>
                                        {/* @ts-ignore */}
                                          {youtubeTopics}
                                        </p>
                                      </Card>
                                </AccordionTab>
                            </Accordion>
                        </div>
                      </>

                      ):
                  // (youtubeSummary!="" && youtubeTokenUsage!="") ? (
                  //   <>
                  //   <Box
                  //       height="auto"
                  //       alignContent="center"
                  //       sx={{
                  //         width: '60%',
                  //         left: '0',
                  //         right: '0',
                  //         margin: 'auto',
                  //         bottom: 10,
                  //       }}
                  //     >
                  //          <div className="card flex flex-wrap gap-2 justify-content-center">
                  //             <PrimeButton disabled={true} icon="pi pi-youtube" style={{height:"2%", marginRight:"2%"}} className="p-button-danger p-button"></PrimeButton>
                  //             <PrimeMessage severity="info" text={youtubeTitle} />
                  //             <PrimeButton onClick={confirm2} icon="pi pi-times" className="p-button-danger p-button-outlined"></PrimeButton>
                  //           </div>
                  //     </Box>
                  //     <br/>
                  //     <div className="card" style={{width:"70%", justifyContent:"center", alignItems:"center", marginLeft:"10%"}}>
                  //         <Accordion>
                  //             <AccordionTab header="Video Details">
                  //                 {youtube_iframe()}
                  //                 <br/>
                  //                 <Card title="Topics/Contents" >
                  //                     <p className="m-0" style={{"whiteSpace":"pre-wrap"}}>
                  //                     @ts-ignore
                  //                       {youtubeTopics}
                  //                     </p>
                  //                   </Card>
                  //                   <br/>
                  //                 <Card title="Summary" >
                  //                     <p className="m-0" >
                  //                     @ts-ignore
                  //                       {youtubeSummary}
                  //                     </p>
                  //                   </Card>
                  //             </AccordionTab>
                  //         </Accordion>
                  //     </div>
                  //   </>
                  // ):
                ( 
                  <Box
                      height="auto"
                      alignContent="center"
                      sx={{
                        width: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'initial',
                        left: '0',
                        right: '0',
                        margin: 'auto',
                        bottom: 10,
                        // backgroundColor: 'white',
                      }}
                    >
                      <div className="card flex flex-wrap gap-2 justify-content-center">
                        {/* @ts-ignore */}
                          <PrimeButton disabled={true} icon="pi pi-list" style={{height:"2%", marginRight:"1%"}} className="p-button p-button"></PrimeButton>
                          <PrimeMessage severity="info" text={selectedProject} />
                          <PrimeButton onClick={confirm2} icon="pi pi-times" style={{height:"2%"}} className="p-button-danger p-button-outlined"></PrimeButton>
                      </div>
                    </Box>
              )
            }
          </div>
        ):(
          
          // <div>
          //   <Grid container spacing={3} style={{marginLeft:"2%"}}>
          //   <Grid item xs={2}>
          //       <div className="flex items-center justify-center w-full" style={{cursor:"pointer"}}>
          //             <div className="card flex justify-content-center">
          //                 <FileUpload disabled={disabled} uploadLabel="Upload" chooseLabel="Browse" 
          //                             emptyTemplate={
          //                                 <div>
          //                                 <p className="m-0">Select documents to chat with.</p>
          //                                 <p className="m-0">File supported types: TXT, PDF, DOC</p>
          //                                 </div>
          //                             }
          //                               customUpload={true} accept='.txt, .pdf, .docx' mode="basic" auto={true} name="Upload File" maxFileSize={100000000} onUpload={onUpload} uploadHandler={(e) => {
          //                     if (e.files && e.files[0]) {
          //                         setdisabled(true);
          //                         setQuickChatFileName(e.files[0].name)
          //                         handleFile(e.files[0]).then(r => console.log("upload file success and save embedding success"));
          //                         setdisabled(false);
          //                     } 
          //                 }} />
          //                 {
          //                   (quickChatFileName!=undefined && quickChatFileName!="") ? (
          //                     <p className="m-0">{quickChatFileName}</p>
          //                   ) : null 
          //                    (<p className="m-0">supported types: txt, pdf, doc</p>)
          //                 }
          //             </div>
          //       </div>
          //     </Box>
          //     </Grid>
          //     <Grid item xs={1}>
          //             <p style={{textAlign:"center"}}>OR</p>
          //       </Grid>
          //     <Grid item xs={4}>
          //           <span className="p-float-label">
          //               <InputText disabled={disabled} id="sourceurl"  style={{width:"100%"}}  value={youtubeUrl}
          //                onChange={async (e) => {setYoutubeUrl(e.target.value); await load_youtube_url(e.target.value) }} />
          //               <label htmlFor="sourceurl">Source File Url (youtube)</label>
          //           </span>
          
          //       </Grid>
          //     <Grid item xs={1}>
          //             <p style={{textAlign:"center"}}>OR</p>
          //       </Grid>
          //     <Grid item xs={4}>
          //       <div className="card flex justify-content-center" >
          //       <span className="p-float-label">
          //         <PrimeDropdown disabled={disabled} inputId="projects" style={{width:"80%"}} value={selectedProject} onChange={(e) =>  onManualsOptionChanged(e.value)} options={projects} optionLabel="name" className="w-full md:w-14rem" />
          //         <label htmlFor="projects">Select a Project</label>
          //       </span>
          //       </div>
          //     </Grid>
          //     </Grid>
          // </div>
          <div>
            <Box 
                height="auto"
                alignContent="center"
                sx={{
                  width: '10%',
                  // position: 'absolute',
                  left: '0',
                  right: '0',
                  margin: 'auto',
                  bottom: 10,
                  // backgroundColor: 'white',
                }}
              >
              <IconButton
                aria-describedby={id}
                // variant="contained"
                color="primary"
                onClick={handleClick}
              >
                <Typography variant="h6">Options</Typography>
                <ArrowDropDownCircleOutlinedIcon />
              </IconButton>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                style={{display:display}}
              >
                <Box p={2} width={500} display={display} style={{display:display}}>
                  {/* <Button variant="outlined" fullWidth>
                    Browse Local Files or Folders
                  </Button> */}
                  <br/>
                  {/* <Button variant="outlined" fullWidth>
                    Upload YouTube URL
                  </Button> */}
                    <div className="card flex justify-content-center">
                      {/* <FileUpload style={{marginLeft:"35%"}} disabled={disabled} uploadLabel="Upload" chooseLabel="Browse" 
                                      emptyTemplate={
                                          <div>
                                          <p className="m-0">Select documents to chat with.</p>
                                          <p className="m-0">File supported types: TXT, PDF, DOC</p>
                                          </div>
                                      }
                                        customUpload={true} accept='.txt, .pdf, .docx, .wav, .mp3' mode="basic" auto={true} name="Upload File" maxFileSize={100000000} onUpload={onUpload} uploadHandler={async (e) => {
                              if (e.files && e.files[0]) {
                                  setdisabled(true);
                                  setQuickChatFile(e.files[0].name)
                                  await handleFile(e.files).then(r => console.log("upload file success and save embedding success"));
                                  setdisabled(false);
                              } 
                          }} /> */}
                      </div>
                      <br/>
                      <div className="card flex justify-content-center" style={{display:display}}>
                        <Grid container spacing={0} style={{marginLeft:"2%"}}>
                          <Grid item xs={1} style={{alignItems:"center",marginTop:"2%"}}>
                            <SubscriptionsOutlinedIcon/>
                          </Grid>
                          <Grid item xs={10}>
                            <span className="p-float-label">
                                  <InputText disabled={disabled} id="sourceurl"  style={{width:"100%"}}  value={youtubeUrl}
                                  onChange={async (e) => {setYoutubeUrl(e.target.value); setYoutubeTitle(youtubeTitle); handleClose(); await load_youtube_url(e.target.value) }} />
                                  <label htmlFor="sourceurl">Source File Url (youtube)</label>
                              </span>
                          </Grid>
                        </Grid>
                      </div>
                  <br/>
                  <div className="card flex flex-wrap gap-2 justify-content-center" style={{display:display}}>
                      <Grid container spacing={0} style={{marginLeft:"2%"}} display={display}>
                        <Grid item xs={1} style={{alignItems:"center",marginTop:"2%"}}>
                          <ListOutlinedIcon/>
                        </Grid>
                        <Grid item xs={10}>
                          {/* <span className="p-float-label">
                            <PrimeDropdown disabled={disabled} inputId="projects" style={{width:"100%"}} value={selectedProject} onChange={(e) =>  onManualsOptionChanged(e.value)} options={projects} optionLabel="name" className="w-full md:w-14rem" />
                            <label htmlFor="projects">Select a Project</label>
                          </span> */}
                          <TextField
                              id="outlined-select-currency-native"
                              select
                              label="Select a Project"
                              value={selectedProject || ""}
                              SelectProps={{
                                native: true,
                              }}
                              onChange={(e) =>  onManualsOptionChanged(e.target.value)}
                            >
                              {
                              // projects.map((option) => (
                              //   <option key={option.name} value={option.name}>
                              //     {option.name}
                              //   </option>
                              projectsDetails.map((item) => (
                                // @ts-ignore
                                <option key={item.label} value={item.label}>
                                  {/* @ts-ignore */}
                                    {item.label}
                                </option>
                              ))}
                            </TextField>
                        </Grid>  
                      </Grid> 
                 </div>
                </Box>
            </Popover>
          </Box>
        </div>
        )
      }              
      <Grid
        container
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="stretch"
        px={4}
        pb={4}
        sx={{
          flexGrow: 1,
          position: 'relative',
          width: '100%',
          margin: 'auto',
          left: '0',
          right: '0', 
        }}
        height={(quickChatFile==undefined && youtubeTitle==undefined && selectedProject==undefined 
                && quickChatFile=="" && youtubeTitle=="" && selectedProject=="" ) ? "93%" : (selectedProject!="" && selectedProject!=undefined) ? "90%": "80%"}
      >

        <Grid item overflow="scroll" xs={12} md={12} height="100%">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {
          (indexes.includes(selectedProject) || isUploading) ? (null
            ) : (
              <ToggleButtonGroup
                color="primary"
                value={llmmodel}
                exclusive
                onChange={handleChange}
                aria-label="Platform"
              >
                {process.env.REACT_APP_ENABLE_VISION?(<ToggleButton value="GPT4VISION">GPT 4 Vision</ToggleButton>):null}
                {/* <ToggleButton value="GPT4VISION">GPT 4 Vision</ToggleButton> */}
                <ToggleButton value="GPT4">GPT 4 Turbo</ToggleButton>
                <ToggleButton value="GPT4-32k-model">GPT 4</ToggleButton>
                {
                  (youtubeTitle=="") ? 
                  (<ToggleButton value="claude-3-opus-20240229">Claude 3 Opus</ToggleButton> ) : null
                }
                <ToggleButton value="GPT3516K">GPT 3.5</ToggleButton>
                {
                  (!hyde_indexes.includes(selectedProject) || selectedProject=="") ?
                  (<ToggleButton value="llama2">Llama 2</ToggleButton>) : null
                }
              </ToggleButtonGroup>
            )
        }
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <p>Display Cost:</p>&nbsp;&nbsp;
            <ToggleButtonGroup
                color="primary"
                value={costDisplay}
                exclusive
                onChange={handleCostToggle}
                aria-label="Cost"
              >
                <ToggleButton value="true">On</ToggleButton>
                <ToggleButton value="false">Off</ToggleButton>
              </ToggleButtonGroup>
        </div>
        </div>
          {/* <br/>
          <br/> */}
          {/* && (quickChatFile!=undefined && quickChatFile!='' ) || (selectedProject!=undefined && selectedProject!="")  */}
          {!lastQuestionRef.current ? (
            // <Grid item>
            //   <Grid container direction="column" justifyContent="space-between" alignItems="center">
            //     <Grid item>
            //       <SparkleFilled
            //         fontSize={'100px'}
            //         primaryFill={'rgba(115, 118, 225, 1)'}
            //         aria-hidden="true"
            //         aria-label="Chat logo"
            //       />
            //     </Grid>
            //     <Grid item>
            //       <Typography variant="h3" component="div" gutterBottom align="center">
            //         Ask anything
            //       </Typography>
            //     </Grid>
            //   </Grid>
            // </Grid>
              <div className={styles.chatEmptyState}>
                  <SparkleFilled fontSize={"100px"} primaryFill={"rgba(115, 118, 225, 1)"} aria-hidden="true" aria-label="Chat logo" />
                  <h1 className={styles.chatEmptyStateTitle}>Chat with your data</h1>
                  <h2 className={styles.chatEmptyStateSubtitle}>Ask anything or try an example</h2> 
                  {getDocumentOptions()}
              </div>
          ) : (
            <div>
              <Grid container item xs={12} md={12} justifyContent="flex-end" height="5%">
                <ClearChatButton
                  className={styles.commandButton}
                  onClick={clearChat}
                  disabled={!lastQuestionRef.current || isLoading}
                />
              </Grid>
              <Grid
                container
                direction="column"
                justifyContent="space-between"
                alignItems="stretch"
                sx={{
                  overflowY: 'auto',
                  height: '90%',
                }}
              >
                {/* @ts-ignore */}
                {answers.map((answer, index) => (
                  <div key={index}>
                    <UserChatMessage message={answer[0]} image=''/>
                    <div className={styles.chatMessageGpt}>
                      <Answer
                        key={index}
                        answer={answer[1]}
                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                        onCitationClicked={(c) => onShowCitation(c, index)}
                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                        onFollowupQuestionClicked={(q) => makeApiRequest(q, null, null)}
                        showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                        displayCost={costDisplay}
                      />
                    </div>
                    {/* {isStreaming &&
                                streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={streamedAnswer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={true}
                                                key={index}
                                                answer={streamedAnswer[1]}
                                                isSelected={false}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                            />
                                        </div>
                                    </div>
                                ))}
                    {!isStreaming &&
                        answers.map((answer, index) => (
                            <div key={index}>
                                <UserChatMessage message={answer[0]} />
                                <div className={styles.chatMessageGpt}>
                                    <Answer
                                        isStreaming={false}
                                        key={index}
                                        answer={answer[1]}
                                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                        onCitationClicked={c => onShowCitation(c, index)}
                                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                        showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                    />
                                </div>
                            </div>
                        ))} */}

                    <div className="feedback-buttons">
                      <button
                        className={`feedback-button ${isThumbsUp ? "active" : ""}`}
                        onClick={handleThumbsUpClick}
                      >
                        <ThumbUpIcon />
                      </button>
                      <button
                        className={`feedback-button ${isThumbsDown ? "active" : ""}`}
                        onClick={handleThumbsDownClick}
                      >
                        <ThumbDownIcon />
                      </button>
                    </div>

                  </div>
                  
                ))}
                {isLoading && (
                  <>
                    <UserChatMessage message={lastQuestionRef.current} image={lastImageRef.current} />
                    <div className={styles.chatMessageGptMinWidth}>
                      <AnswerLoading />
                    </div>
                  </>
                )}
                {chatError ? (
                  <>
                    <UserChatMessage message={lastQuestionRef.current} image={lastImageRef.current}/>
                    <div className={styles.chatMessageGptMinWidth}>
                      <AnswerError error={"Something went wrong. Please try again."} onRetry={() => makeApiRequest(lastQuestionRef.current, null, null)} />
                    </div>
                  </>
                ) : null}
                <div ref={chatMessageStreamEnd} />
              </Grid>
            </div>
          )}
        </Grid>
        <div>
        <Box
          // height="auto"
          alignContent="center"
          sx={{
            width: '100%',
            position: 'absolute',
            left: '0',
            right: '0',
            // margin: 'auto',
            bottom: 0,
            // backgroundColor: 'white',
          }}
        >
          {/* <Grid container spacing={0} justifyContent={"flex-start"}>
            <Grid item xs={1}> */}
                {/* <div className="flex items-center justify-center w-full" style={{cursor:"pointer"}}>
                      <div className="card flex justify-content-center"> */}
                          {/* <p className="m-0">txt, pdf, doc</p> */}
                          {/* {
                            (quickChatFileName!=undefined && quickChatFileName!="") ? (
                              <p className="m-0">{quickChatFileName}</p>
                            ) : (<p className="m-0">supported types: txt, pdf, doc</p>)
                          } */}
                      {/* </div>
                </div> */}
              {/* </Box> */}
              {/* </Grid>
              <Grid item xs={11}> */}
                <div className={styles.chatInput} style={{ display: 'flex' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: '8px', // Adjust as needed
                      borderRadius: '50%',
                      padding: '8px', // Adjust padding as needed
                      transition: 'background-color 0.3s ease',
                      //@ts-ignore
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)', // Fill color on hover
                      },
                    }}
                  >
                    {/* Browse icon */}
                    <HighlightOnHover>
                      <MTooltip title="Browse" placement="top">
                        <div
                          className={`${styles.questionInputSendButton}`}
                          aria-label="Browse files"
                          onClick={handleBrowseClick}
                        >
                          <AddCircleIcon className="highlightIcon" />
                        </div>
                      </MTooltip>
                    </HighlightOnHover>
                  </div>
                  <QuestionInput
                    clearOnSend
                    placeholder={'Type a new question'}
                    disabled={isLoading}
                    llmmodel={llmmodel}
                    onSend={(question, uploadedFile, uploadedImage) => {
                        makeApiRequest(question, uploadedFile, uploadedImage)
                      }
                    }
                  />
              </div>
              {/* </Grid>
            </Grid> */}
          <input
            type="file"
            accept=".txt, .pdf, .docx, .wav, .mp3"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={ async (e) => {
              setdisabled(true);
              console.log(e)
              //@ts-ignore
              setQuickChatFile(e.target.files?.[0].name)
              //@ts-ignore
              await handleFile([e.target.files?.[0]]).then(r => console.log("upload file success and save embedding success"));
              setdisabled(false);
              //await handleFileChange(e)
              }
            }
          />
        </Box>
        </div>
        
      </Grid>
      {answers.length > 0 && activeAnalysisPanelTab && (
        <AnalysisPanel
          className={styles.chatAnalysisPanel}
          activeCitation={activeCitation}
          onActiveTabChanged={(x) => onToggleTab(x, selectedAnswer)}
          citationHeight="810px"
          answer={answers[selectedAnswer][1]}
          activeTab={activeAnalysisPanelTab}
          visibility={true}
        />
      )}

      <Panel
        headerText="Configure answer generation"
        isOpen={isConfigPanelOpen}
        isBlocking={false}
        onDismiss={() => setIsConfigPanelOpen(false)}
        closeButtonAriaLabel="Close"
        onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
        isFooterAtBottom={true}
      >
        <FluentTextField
          className={styles.chatSettingsSeparator}
          defaultValue={promptTemplate}
          label="Override prompt template"
          multiline
          autoAdjustHeight
          onChange={onPromptTemplateChange}
        />
      </Panel>
    </>
  );
};

export default Chat;
